/**
 * CTLRichControls - Shared rich UI controls used across the Close The Loop
 * "Track Requests" tabs (All Submissions, DCR Requests, etc.).
 *
 * These were extracted from CloseTheLoopPage.tsx's AllSubmissionsView so that
 * other CTL tabs (like DCRRequests) can reuse the exact same search box,
 * filter pill, and share-link components/styling instead of diverging.
 */
import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import {
    IContextualMenuItem,
    DefaultButton,
    PrimaryButton,
    SearchBox,
    Stack,
    IContextualMenuListProps,
    IRenderFunction
} from "@fluentui/react";
import { FilterPill } from "@m365-admin/in-page-filter";
import { Box, Typography, Tooltip, Button, IconButton } from "@mui/material";
import {
    Checkmark16Regular,
    Search20Regular,
    Dismiss20Regular,
    CalendarLtr20Regular,
    Share20Regular
} from "@fluentui/react-icons";
import { CustomFilterPill } from "../../../SharedComponents/CustomFilterPill";
import { CUSTOMER_SEARCH_STYLES } from "../../CustomerSearch/CustomerSearch.styles";
import { THEME } from "./theme";

// Helper to convert options to menu items for CustomFilterPill
export const mapToMenuItems = (options: string[]): IContextualMenuItem[] =>
    options.map((value) => ({ key: value, text: value }));

// Filter pill styles matching ExploreCustomers
export const FILTER_PILL_STYLES = CUSTOMER_SEARCH_STYLES.CUSTOMER_SEARCH_FILTER_PILL.STYLES;

/**
 * RichSearchBox - Prominent, animated search input used at the top of CTL grids.
 */
export const RichSearchBox: React.FC<{
    value: string;
    onChange: (value: string) => void;
    onClear: () => void;
    placeholder?: string;
    showHint?: boolean;
}> = ({ value, onChange, onClear, placeholder, showHint }) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
        <Box sx={{ width: "60%", mx: "auto", mb: 2 }}>
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    background: isFocused
                        ? "linear-gradient(135deg, #FFFFFF 0%, #F0F7FF 100%)"
                        : "linear-gradient(135deg, #FAFBFC 0%, #F5F7FA 100%)",
                    borderRadius: "12px",
                    border: isFocused ? "2px solid #0078D4" : "1px solid #E1E5EB",
                    boxShadow: isFocused
                        ? "0 8px 32px rgba(0, 120, 212, 0.2), 0 2px 8px rgba(0, 0, 0, 0.08)"
                        : "0 2px 12px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    overflow: "hidden",
                    "&:hover": {
                        boxShadow: "0 6px 24px rgba(0, 120, 212, 0.15), 0 2px 8px rgba(0, 0, 0, 0.06)",
                        borderColor: isFocused ? "#0078D4" : "#B8D4E8"
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pl: 2,
                        pr: 1,
                        color: isFocused ? "#0078D4" : "#8A8A9A",
                        transition: "color 0.3s ease"
                    }}
                >
                    <Search20Regular />
                </Box>

                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        padding: "14px 8px",
                        fontSize: "14px",
                        color: "#1A1A2E",
                        fontFamily: "inherit"
                    }}
                />

                {value && (
                    <IconButton
                        size="small"
                        onClick={onClear}
                        sx={{
                            mr: 1,
                            color: "#8A8A9A",
                            "&:hover": {
                                color: "#0078D4",
                                backgroundColor: "rgba(0, 120, 212, 0.08)"
                            }
                        }}
                    >
                        <Dismiss20Regular />
                    </IconButton>
                )}
            </Box>

            {showHint && (
                <Typography
                    sx={{
                        textAlign: "center",
                        color: THEME.textSecondary,
                        fontSize: "12px",
                        mt: 1,
                        opacity: 0.8
                    }}
                >
                    Type at least 3 characters to search
                </Typography>
            )}
        </Box>
    );
};

interface CTLFilterPillProps {
    name: string;
    options: string[];
    selectedValues?: string[];
    onApply: (selectedItems: string[] | undefined) => void;
    urlParamKey?: string;
    showSelectAll?: boolean;
}

/**
 * CTLFilterPill - Wrapper for CustomFilterPill with searchable dropdown
 * Passes ALL options to CustomFilterPill - it handles search internally
 * Only renders when there are options available
 * Now accepts selectedValues to maintain controlled state from parent
 * Supports URL persistence via urlParamKey for sharing
 * Now includes Select All button to select all options at once
 */
export const CTLFilterPill: React.FC<CTLFilterPillProps> = ({ name, options, selectedValues, onApply, urlParamKey, showSelectAll = true }) => {
    const safeOptions = useMemo(() => options || [], [options]);
    const menuItems = useMemo(() => mapToMenuItems(safeOptions), [safeOptions]);

    const selectedKeys = useMemo(() => {
        if (!selectedValues || selectedValues.length === 0) return undefined;
        return selectedValues.reduce((acc, value) => {
            acc[value] = true;
            return acc;
        }, {} as { [key: string]: boolean });
    }, [selectedValues]);

    if (safeOptions.length === 0) {
        return null;
    }

    return (
        <CustomFilterPill
            defaultValue="All"
            isRemovable={true}
            name={name}
            styles={FILTER_PILL_STYLES}
            selectedKeys={selectedKeys}
            urlParamKey={urlParamKey}
            useGroupedFilters={true}
            menuProps={{
                focusZoneProps: {
                    shouldInputLoseFocusOnArrowKey: () => true
                },
                items: menuItems
            }}
            onApply={onApply}
            onCancel={onApply}
            onResetFilters={() => onApply([])}
            selectAllButton={showSelectAll && safeOptions.length > 0 ? {
                show: true,
                label: "Select All",
                selectValues: safeOptions
            } : undefined}
        />
    );
};

interface CTLDateRangeFilterPillProps {
    name: string;
    onApply: (fromDate: string, toDate: string) => void;
}

/**
 * CTLDateRangeFilterPill - Date range filter pill
 * Shows from/to date inputs for filtering
 */
export const CTLDateRangeFilterPill: React.FC<CTLDateRangeFilterPillProps> = ({ name, onApply }) => {
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [appliedFrom, setAppliedFrom] = useState("");
    const [appliedTo, setAppliedTo] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    const handleApply = useCallback(() => {
        setAppliedFrom(fromDate);
        setAppliedTo(toDate);
        onApply(fromDate, toDate);
        containerRef.current?.click();
    }, [fromDate, toDate, onApply]);

    const handleCancel = useCallback(() => {
        setFromDate(appliedFrom);
        setToDate(appliedTo);
        containerRef.current?.click();
    }, [appliedFrom, appliedTo]);

    const handleReset = useCallback(() => {
        setFromDate("");
        setToDate("");
        setAppliedFrom("");
        setAppliedTo("");
        onApply("", "");
    }, [onApply]);

    const formatDisplayDate = (dateStr: string): string => {
        if (!dateStr) return "";
        const date = new Date(dateStr + "T00:00:00");
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const renderMenuList: IRenderFunction<IContextualMenuListProps> = useCallback(
        () => (
            <>
                <div style={{ padding: 16, minWidth: 320, background: "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 100%)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            backgroundColor: `${THEME.primary}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <CalendarLtr20Regular style={{ color: THEME.primary, width: 18, height: 18 }} />
                        </Box>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: THEME.textPrimary }}>
                            {name}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: THEME.textSecondary, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.5px" }}>From</Typography>
                            <Box sx={{
                                position: "relative",
                                "&:focus-within": {
                                    "& .date-input-box": {
                                        borderColor: THEME.primary,
                                        boxShadow: `0 0 0 3px ${THEME.primary}20`
                                    }
                                }
                            }}>
                                <Box
                                    className="date-input-box"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        border: `1px solid ${THEME.border}`,
                                        borderRadius: "8px",
                                        backgroundColor: "white",
                                        transition: "all 0.2s ease",
                                        overflow: "hidden"
                                    }}
                                >
                                    <Box sx={{ pl: 1.5, display: "flex", alignItems: "center" }}>
                                        <CalendarLtr20Regular style={{ color: THEME.textSecondary, width: 16, height: 16 }} />
                                    </Box>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        max={toDate || undefined}
                                        style={{
                                            flex: 1,
                                            padding: "10px 12px 10px 8px",
                                            fontSize: "13px",
                                            border: "none",
                                            outline: "none",
                                            backgroundColor: "transparent",
                                            color: fromDate ? THEME.textPrimary : THEME.textSecondary,
                                            cursor: "pointer"
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", pt: 2.5 }}>
                            <Typography sx={{ color: THEME.textSecondary, fontSize: "12px", fontWeight: 500 }}>to</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: THEME.textSecondary, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.5px" }}>To</Typography>
                            <Box sx={{
                                position: "relative",
                                "&:focus-within": {
                                    "& .date-input-box": {
                                        borderColor: THEME.primary,
                                        boxShadow: `0 0 0 3px ${THEME.primary}20`
                                    }
                                }
                            }}>
                                <Box
                                    className="date-input-box"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        border: `1px solid ${THEME.border}`,
                                        borderRadius: "8px",
                                        backgroundColor: "white",
                                        transition: "all 0.2s ease",
                                        overflow: "hidden"
                                    }}
                                >
                                    <Box sx={{ pl: 1.5, display: "flex", alignItems: "center" }}>
                                        <CalendarLtr20Regular style={{ color: THEME.textSecondary, width: 16, height: 16 }} />
                                    </Box>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        min={fromDate || undefined}
                                        style={{
                                            flex: 1,
                                            padding: "10px 12px 10px 8px",
                                            fontSize: "13px",
                                            border: "none",
                                            outline: "none",
                                            backgroundColor: "transparent",
                                            color: toDate ? THEME.textPrimary : THEME.textSecondary,
                                            cursor: "pointer"
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${THEME.border}` }}>
                        <Typography sx={{ fontSize: "11px", fontWeight: 500, color: THEME.textSecondary, mb: 1, textTransform: "uppercase", letterSpacing: "0.5px" }}>Quick Select</Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {[
                                { label: "Last 7 days", days: 7 },
                                { label: "Last 30 days", days: 30 },
                                { label: "Last 90 days", days: 90 },
                                { label: "Last year", days: 365 }
                            ].map(({ label, days }) => (
                                <Box
                                    key={label}
                                    onClick={() => {
                                        const to = new Date();
                                        const from = new Date();
                                        from.setDate(from.getDate() - days);
                                        setFromDate(from.toISOString().split("T")[0]);
                                        setToDate(to.toISOString().split("T")[0]);
                                    }}
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        fontSize: "12px",
                                        color: THEME.primary,
                                        backgroundColor: `${THEME.primary}08`,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        transition: "all 0.15s ease",
                                        fontWeight: 500,
                                        "&:hover": {
                                            backgroundColor: `${THEME.primary}15`
                                        }
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </div>
                <Stack
                    horizontal
                    tokens={{ childrenGap: 10 }}
                    styles={{ root: { borderTop: `1px solid ${THEME.border}`, padding: 12, backgroundColor: "#FAFBFC" } }}
                >
                    <PrimaryButton
                        text="Apply"
                        onClick={handleApply}
                        disabled={!fromDate && !toDate}
                        styles={{ root: { borderRadius: 6 } }}
                    />
                    <DefaultButton
                        text="Cancel"
                        onClick={handleCancel}
                        styles={{ root: { borderRadius: 6 } }}
                    />
                </Stack>
            </>
        ),
        [name, fromDate, toDate, handleApply, handleCancel]
    );

    const displayValue = (appliedFrom || appliedTo)
        ? [`${formatDisplayDate(appliedFrom) || "Start"} - ${formatDisplayDate(appliedTo) || "End"}`]
        : undefined;

    return (
        <div ref={containerRef}>
            <FilterPill
                name={name}
                defaultValue="All"
                value={displayValue}
                isRemovable={true}
                styles={FILTER_PILL_STYLES}
                menuProps={{
                    items: [{ key: "dates", text: "" }],
                    onRenderMenuList: renderMenuList
                }}
                resetProps={{
                    onClick: handleReset,
                    "aria-label": `Clear ${name} filter`
                }}
            />
        </div>
    );
};

/**
 * ShareFilterButton - Rich button to copy current page URL with filters
 * Easy to understand with text label and icon
 */
export const ShareFilterButton: React.FC = () => {
    const [copied, setCopied] = useState(false);

    const handleShare = useCallback(() => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    }, []);

    return (
        <Tooltip title={copied ? "Link copied to clipboard!" : "Copy link with current filters to share"}>
            <Button
                onClick={handleShare}
                variant="outlined"
                size="small"
                startIcon={copied ? <Checkmark16Regular /> : <Share20Regular />}
                sx={{
                    ml: 1.5,
                    px: 2,
                    py: 0.75,
                    minWidth: "auto",
                    textTransform: "none",
                    fontSize: "13px",
                    fontWeight: 600,
                    borderRadius: "8px",
                    backgroundColor: copied ? "#DFF6DD" : "#E7F1FF",
                    borderColor: copied ? THEME.success : THEME.primary,
                    color: copied ? THEME.success : THEME.primary,
                    boxShadow: copied ? "0 2px 8px rgba(16, 124, 16, 0.2)" : "0 2px 8px rgba(0, 120, 212, 0.15)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        backgroundColor: copied ? "#C8EBC5" : "#D0E4FF",
                        borderColor: copied ? THEME.success : THEME.primaryDark,
                        boxShadow: copied ? "0 4px 12px rgba(16, 124, 16, 0.25)" : "0 4px 12px rgba(0, 120, 212, 0.25)"
                    }
                }}
            >
                {copied ? "Copied!" : "Share"}
            </Button>
        </Tooltip>
    );
};

interface CTLSearchableFilterPillProps {
    name: string;
    options: string[];
    selectedValues?: string[];
    onApply: (selectedItems: string[] | undefined) => void;
    maxVisibleItems?: number;
}

/**
 * CTLSearchableFilterPill - Optimized filter pill for large datasets
 * Features: search box, virtualized list (renders only visible items), checkbox selection
 * Now accepts selectedValues to maintain controlled state from parent
 */
export const CTLSearchableFilterPill: React.FC<CTLSearchableFilterPillProps> = ({ name, options, selectedValues, onApply, maxVisibleItems = 50 }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set(selectedValues || []));
    const [appliedItems, setAppliedItems] = useState<Set<string>>(() => new Set(selectedValues || []));
    const [visibleCount, setVisibleCount] = useState(maxVisibleItems);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const newSet = new Set(selectedValues || []);
        setSelectedItems(newSet);
        setAppliedItems(newSet);
    }, [selectedValues]);

    const safeOptions = useMemo(() => options || [], [options]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return safeOptions;
        const term = searchTerm.toLowerCase();
        return safeOptions.filter(opt => opt.toLowerCase().includes(term));
    }, [safeOptions, searchTerm]);

    const visibleOptions = useMemo(() => filteredOptions.slice(0, visibleCount), [filteredOptions, visibleCount]);
    const hasMore = filteredOptions.length > visibleCount;

    useEffect(() => {
        setVisibleCount(maxVisibleItems);
    }, [searchTerm, maxVisibleItems]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20 && hasMore) {
            setVisibleCount(prev => Math.min(prev + maxVisibleItems, filteredOptions.length));
        }
    }, [hasMore, filteredOptions.length, maxVisibleItems]);

    const handleToggle = useCallback((item: string) => {
        setSelectedItems(prev => {
            const next = new Set(prev);
            if (next.has(item)) {
                next.delete(item);
            } else {
                next.add(item);
            }
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedItems(new Set(filteredOptions));
    }, [filteredOptions]);

    const handleClearAll = useCallback(() => {
        setSelectedItems(new Set());
    }, []);

    const handleApply = useCallback(() => {
        setAppliedItems(new Set(selectedItems));
        onApply(selectedItems.size > 0 ? Array.from(selectedItems) : undefined);
        containerRef.current?.click();
    }, [selectedItems, onApply]);

    const handleCancel = useCallback(() => {
        setSelectedItems(new Set(appliedItems));
        setSearchTerm("");
        containerRef.current?.click();
    }, [appliedItems]);

    const handleReset = useCallback(() => {
        setSelectedItems(new Set());
        setAppliedItems(new Set());
        setSearchTerm("");
        onApply([]);
    }, [onApply]);

    const renderMenuList: IRenderFunction<IContextualMenuListProps> = useCallback(
        () => (
            <Box sx={{ width: 280, maxHeight: 400, display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${THEME.border}` }}>
                    <SearchBox
                        placeholder={`Search ${name}...`}
                        value={searchTerm}
                        onChange={(_, newValue) => setSearchTerm(newValue || "")}
                        autoFocus
                        styles={{ root: { width: "100%" } }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, fontSize: "11px" }}>
                        <Typography
                            component="span"
                            sx={{ color: THEME.primary, cursor: "pointer", fontSize: "11px", "&:hover": { textDecoration: "underline" } }}
                            onClick={handleSelectAll}
                        >
                            Select All ({filteredOptions.length})
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ color: THEME.textSecondary, cursor: "pointer", fontSize: "11px", "&:hover": { textDecoration: "underline" } }}
                            onClick={handleClearAll}
                        >
                            Clear
                        </Typography>
                    </Box>
                </Box>

                <Box
                    ref={listRef}
                    onScroll={handleScroll}
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        maxHeight: 250,
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-thumb": { background: "#C1C1C1", borderRadius: "3px" }
                    }}
                >
                    {visibleOptions.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: "center", color: THEME.textSecondary, fontSize: "12px" }}>
                            No options found
                        </Box>
                    ) : (
                        visibleOptions.map(item => (
                            <Box
                                key={item}
                                onClick={() => handleToggle(item)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    cursor: "pointer",
                                    backgroundColor: selectedItems.has(item) ? "#E5F1FB" : "transparent",
                                    "&:hover": { backgroundColor: selectedItems.has(item) ? "#D0E4F7" : "#F5F5F5" }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        border: `2px solid ${selectedItems.has(item) ? THEME.primary : THEME.border}`,
                                        borderRadius: "3px",
                                        backgroundColor: selectedItems.has(item) ? THEME.primary : "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}
                                >
                                    {selectedItems.has(item) && (
                                        <span style={{ color: "#fff", fontSize: "10px", fontWeight: "bold" }}>✓</span>
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: THEME.textPrimary,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                    title={item}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))
                    )}
                    {hasMore && (
                        <Box sx={{ p: 1, textAlign: "center", color: THEME.textSecondary, fontSize: "11px", fontStyle: "italic" }}>
                            Scroll for more... ({filteredOptions.length - visibleCount} remaining)
                        </Box>
                    )}
                </Box>

                <Stack
                    horizontal
                    tokens={{ childrenGap: 10 }}
                    styles={{ root: { borderTop: `1px solid ${THEME.border}`, padding: 10, justifyContent: "space-between" } }}
                >
                    <Box sx={{ fontSize: "11px", color: THEME.textSecondary, alignSelf: "center" }}>
                        {selectedItems.size} selected
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <PrimaryButton text="Apply" onClick={handleApply} />
                        <DefaultButton text="Cancel" onClick={handleCancel} />
                    </Box>
                </Stack>
            </Box>
        ),
        [name, searchTerm, filteredOptions, visibleOptions, selectedItems, hasMore, handleScroll, handleToggle, handleSelectAll, handleClearAll, handleApply, handleCancel]
    );

    const displayValue = appliedItems.size > 0 ? Array.from(appliedItems) : undefined;

    if (safeOptions.length === 0) {
        return null;
    }

    return (
        <div ref={containerRef}>
            <FilterPill
                name={name}
                defaultValue="All"
                value={displayValue}
                isRemovable={true}
                styles={FILTER_PILL_STYLES}
                menuProps={{
                    items: [{ key: "searchable", text: "" }],
                    onRenderMenuList: renderMenuList
                }}
                resetProps={{
                    onClick: handleReset,
                    "aria-label": `Clear ${name} filter`
                }}
            />
        </div>
    );
};
